import { Input } from '@heidi/design-system/input';
import { Label } from '@heidi/design-system/label';
import { Textarea } from '@heidi/design-system/textarea';
import { toast } from '@heidi/design-system/toast';
import { TypographyP2 } from '@heidi/design-system/typography';
import { Calendar, FileText } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import { sharedAgentMessages } from '../../shared.messages';

import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';
import { InlineApprovalActions } from './inline-approval-actions';
import { InlineApprovalCardShell } from './inline-approval-card-shell';
import { LabelledContextPanel } from './labelled-context-panel';
import {
  MailComposeApprovalLayout,
  type MailComposeValues,
} from './mail-compose-approval-layout';
import {
  buildCalendarModifiedInput,
  buildFileModifiedInput,
  buildMailModifiedInput,
  formatAddressList,
  nangoConnectorApprovalKind,
  parseAddressListInput,
  readCalendarApprovalFields,
  readFileApprovalFields,
  readMailApprovalFields,
  readMailReplyPreview,
  readWorkspaceWriteApprovalFields,
  resolveDatetimeForApprove,
  toDatetimeLocalValue,
  type CalendarApprovalFields,
  type FileApprovalFields,
  type MailApprovalFields,
  type MailReplyApprovalPreview,
  type NangoWorkspaceWriteAction,
  type WorkspaceWriteApprovalFields,
} from './nango-connector-approval';

export type NangoConnectorApprovalResolve = (
  approvalId: string,
  verdict: 'approve' | 'deny',
  reason?: string,
  modifiedInput?: Record<string, unknown> | null
) => Promise<void>;

export interface NangoConnectorApprovalCardProps {
  approval: ApprovalRequestInput;
  onResolve: NangoConnectorApprovalResolve;
}

type PendingVerdict = 'approve' | 'deny' | null;

export function useNangoApprovalSubmit(
  approvalId: string,
  onResolve: NangoConnectorApprovalResolve
): {
  pendingVerdict: PendingVerdict;
  isBusy: boolean;
  submit: (
    verdict: 'approve' | 'deny',
    modifiedInput?: Record<string, unknown> | null
  ) => void;
} {
  const intl = useIntl();
  const [pendingVerdict, setPendingVerdict] = useState<PendingVerdict>(null);

  const submit = (
    verdict: 'approve' | 'deny',
    modifiedInput?: Record<string, unknown> | null
  ) => {
    if (pendingVerdict !== null) return;
    setPendingVerdict(verdict);
    void onResolve(approvalId, verdict, undefined, modifiedInput ?? null).catch(
      (error: unknown) => {
        setPendingVerdict(null);
        toast({
          title: intl.formatMessage(sharedAgentMessages.couldntResolve),
          description:
            error instanceof Error
              ? error.message
              : intl.formatMessage({
                  defaultMessage: 'Unknown error',
                  id: 'qDwvZ4p3Io',
                }),
          tone: 'destructive',
        });
      }
    );
  };

  return { pendingVerdict, isBusy: pendingVerdict !== null, submit };
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

export function NangoConnectorApprovalCard({
  approval,
  onResolve,
}: NangoConnectorApprovalCardProps): JSX.Element | null {
  const kind = nangoConnectorApprovalKind(approval.tool_name, approval.tool_input);
  if (kind === 'mail') {
    return <MailApprovalCard approval={approval} onResolve={onResolve} />;
  }
  if (kind === 'calendar') {
    return <CalendarApprovalCard approval={approval} onResolve={onResolve} />;
  }
  if (kind === 'file') {
    return <FileApprovalCard approval={approval} onResolve={onResolve} />;
  }
  if (kind === 'workspace') {
    return <WorkspaceWriteApprovalCard approval={approval} onResolve={onResolve} />;
  }
  return null;
}

function MailApprovalCard({ approval, onResolve }: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const initial = useMemo(
    () => readMailApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );
  const replyPreview = useMemo(
    () => readMailReplyPreview(approval.context),
    [approval.context]
  );

  const [values, setValues] = useState<MailComposeValues>(() => {
    const compose = initial?.kind === 'compose' ? initial : null;
    return {
      to: formatAddressList(compose?.to ?? []),
      cc: formatAddressList(compose?.cc ?? []),
      bcc: formatAddressList(compose?.bcc ?? []),
      subject: compose?.subject ?? '',
      body: initial?.body ?? '',
    };
  });

  if (!initial) return null;

  const isReply = initial.kind === 'reply';
  const isDraft =
    initial.kind === 'compose' && initial.toolName === 'integrations.mail.create_draft';
  const canApprove =
    isReply || isDraft
      ? true
      : parseAddressListInput(values.to).length > 0 &&
        values.subject.trim().length > 0 &&
        values.body.trim().length > 0;

  const currentFields = (): MailApprovalFields =>
    isReply
      ? {
          kind: 'reply',
          toolName: 'integrations.mail.reply',
          messageId: initial.messageId,
          body: values.body,
        }
      : {
          kind: 'compose',
          toolName: initial.toolName,
          to: parseAddressListInput(values.to),
          cc: parseAddressListInput(values.cc),
          bcc: parseAddressListInput(values.bcc),
          subject: values.subject,
          body: values.body,
        };

  const title =
    initial.toolName === 'integrations.mail.create_draft'
      ? intl.formatMessage({
          defaultMessage: 'Save email draft',
          id: 'AQlXn5r7L9',
        })
      : isReply
        ? intl.formatMessage({ defaultMessage: 'Send email reply', id: '0vIVBoAl3q' })
        : intl.formatMessage({ defaultMessage: 'Send email', id: 'sZIoMyo0mu' });

  const approveLabel =
    initial.toolName === 'integrations.mail.create_draft'
      ? intl.formatMessage({ defaultMessage: 'Save draft', id: 'R2zJFpVjmW' })
      : intl.formatMessage({ defaultMessage: 'Send', id: '9WRlF4R2gm' });

  return (
    <MailComposeApprovalLayout
      testId={`nango-mail-approval-${approval.approval_id}`}
      fieldTestIdPrefix="nango-mail-approval"
      title={title}
      values={values}
      onChange={(field, value) => setValues((prev) => ({ ...prev, [field]: value }))}
      headerOverride={
        isReply ? (
          <div className="border-b border-border px-3 py-2">
            <LabelledContextPanel
              label={intl.formatMessage({
                defaultMessage: 'Reply to',
                id: 'P6SLRPKzbr',
              })}
            >
              {replyPreview ? (
                <MailReplyRecipient preview={replyPreview} />
              ) : (
                <TypographyP2 className="break-all font-mono text-sm text-text-secondary">
                  {initial.messageId}
                </TypographyP2>
              )}
            </LabelledContextPanel>
          </div>
        ) : undefined
      }
      isBusy={isBusy}
      canApprove={canApprove}
      approveLabel={approveLabel}
      pendingVerdict={pendingVerdict}
      onApprove={() =>
        submit('approve', buildMailModifiedInput(approval.tool_input, currentFields()))
      }
      onDeny={() => submit('deny')}
    />
  );
}

function MailReplyRecipient({ preview }: { preview: MailReplyApprovalPreview }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-0.5"
      data-testid="nango-mail-approval-reply-to"
    >
      <TypographyP2 className="truncate text-sm text-text-primary">
        {preview.to.join(', ')}
      </TypographyP2>
      {preview.subject ? (
        <TypographyP2 className="truncate text-sm text-text-secondary">
          {preview.subject}
        </TypographyP2>
      ) : null}
    </div>
  );
}

function CalendarApprovalCard({ approval, onResolve }: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const fieldId = useId();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const initial = useMemo(
    () => readCalendarApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );
  const [subject, setSubject] = useState(() => initial?.subject ?? '');
  const [start, setStart] = useState(() => toDatetimeLocalValue(initial?.start));
  const [end, setEnd] = useState(() => toDatetimeLocalValue(initial?.end));
  const [description, setDescription] = useState(() => initial?.description ?? '');
  const [attendees, setAttendees] = useState(() => formatAddressList(initial?.attendees));

  if (!initial) return null;

  const isDelete = initial.toolName === 'integrations.calendar.delete';
  const canApprove = isDelete
    ? Boolean(initial.eventId)
    : initial.toolName === 'integrations.calendar.update'
      ? Boolean(initial.eventId)
      : subject.trim().length > 0 && Boolean(start) && Boolean(end);

  const currentFields = (): CalendarApprovalFields => ({
    ...initial,
    subject,
    start: resolveDatetimeForApprove(initial.start, start),
    end: resolveDatetimeForApprove(initial.end, end),
    description,
    attendees: parseAddressListInput(attendees),
  });

  const title =
    initial.toolName === 'integrations.calendar.delete'
      ? intl.formatMessage({
          defaultMessage: 'Delete calendar event',
          id: 'K5pU3EOdnG',
        })
      : initial.toolName === 'integrations.calendar.update'
        ? intl.formatMessage({
            defaultMessage: 'Update calendar event',
            id: 'c8F3rZ89Kx',
          })
        : intl.formatMessage({
            defaultMessage: 'Create calendar event',
            id: 'jsLlTVyLr6',
          });

  return (
    <InlineApprovalCardShell
      Icon={Calendar}
      title={title}
      subtitle={
        isDelete
          ? intl.formatMessage({
              defaultMessage: 'This may notify attendees',
              id: 'aBU8EJINOY',
            })
          : intl.formatMessage({
              defaultMessage: 'Review and edit before it is saved',
              id: 'giZiped0Ib',
            })
      }
      urgent={!isBusy}
      data-testid={`nango-calendar-approval-${approval.approval_id}`}
    >
      <div className="flex flex-col gap-3">
        {initial.eventId ? (
          <LabelledContextPanel
            label={intl.formatMessage({
              defaultMessage: 'Event',
              id: 'm1czzYTiJ/',
            })}
          >
            <TypographyP2 className="break-all font-mono text-sm text-text-secondary">
              {initial.eventId}
            </TypographyP2>
          </LabelledContextPanel>
        ) : null}
        {isDelete ? null : (
          <>
            <Field
              id={`${fieldId}-title`}
              label={intl.formatMessage({ defaultMessage: 'Title', id: '9a9+wwWy4u' })}
            >
              <Input
                id={`${fieldId}-title`}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-title"
              />
            </Field>
            <Field
              id={`${fieldId}-start`}
              label={intl.formatMessage({ defaultMessage: 'Starts', id: '9anxhqOyRo' })}
            >
              <Input
                id={`${fieldId}-start`}
                type="datetime-local"
                value={start}
                onChange={(event) => setStart(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-start"
              />
            </Field>
            <Field
              id={`${fieldId}-end`}
              label={intl.formatMessage({ defaultMessage: 'Ends', id: '04xGLEkMMo' })}
            >
              <Input
                id={`${fieldId}-end`}
                type="datetime-local"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-end"
              />
            </Field>
            <Field
              id={`${fieldId}-attendees`}
              label={intl.formatMessage({
                defaultMessage: 'Attendees',
                id: '2ow9UmwYzF',
              })}
            >
              <Input
                id={`${fieldId}-attendees`}
                value={attendees}
                onChange={(event) => setAttendees(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-attendees"
              />
            </Field>
            <Field
              id={`${fieldId}-description`}
              label={intl.formatMessage({
                defaultMessage: 'Description',
                id: 'Q8Qw5BZ1VA',
              })}
            >
              <Textarea
                id={`${fieldId}-description`}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                minRows={2}
                maxRows={8}
                autoResize
                disabled={isBusy}
                data-testid="nango-calendar-approval-description"
              />
            </Field>
          </>
        )}
        <InlineApprovalActions
          onApprove={() =>
            submit(
              'approve',
              buildCalendarModifiedInput(approval.tool_input, currentFields())
            )
          }
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
          approveDisabled={!canApprove}
          approveLabel={
            isDelete
              ? intl.formatMessage({ defaultMessage: 'Delete', id: 'K3r6DQW7h+' })
              : undefined
          }
        />
      </div>
    </InlineApprovalCardShell>
  );
}

function FileApprovalCard({ approval, onResolve }: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const fieldId = useId();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const initial = useMemo(
    () => readFileApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );
  const [filename, setFilename] = useState(() => initial?.filename ?? '');

  if (!initial) return null;

  const currentFields = (): FileApprovalFields => ({
    filename,
    folderPath: initial.folderPath,
  });

  return (
    <InlineApprovalCardShell
      Icon={FileText}
      title={intl.formatMessage({
        defaultMessage: 'Save file',
        id: 'F2ujCMSH3z',
      })}
      subtitle={intl.formatMessage({
        defaultMessage: 'Review the file name before it is saved',
        id: 'c+b0oz8cQ7',
      })}
      urgent={!isBusy}
      data-testid={`nango-file-approval-${approval.approval_id}`}
    >
      <div className="flex flex-col gap-3">
        <Field
          id={`${fieldId}-filename`}
          label={intl.formatMessage({ defaultMessage: 'File name', id: 'ppAn7OQ0yB' })}
        >
          <Input
            id={`${fieldId}-filename`}
            value={filename}
            onChange={(event) => setFilename(event.target.value)}
            disabled={isBusy}
            data-testid="nango-file-approval-filename"
          />
        </Field>
        {initial.folderPath ? (
          <TypographyP2 className="text-text-secondary">
            {intl.formatMessage(
              {
                defaultMessage: 'Folder: {folder}',
                id: 'qGw+2biPT5',
              },
              { folder: initial.folderPath }
            )}
          </TypographyP2>
        ) : null}
        <InlineApprovalActions
          onApprove={() =>
            submit(
              'approve',
              buildFileModifiedInput(approval.tool_input, currentFields())
            )
          }
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
          approveDisabled={filename.trim().length === 0}
        />
      </div>
    </InlineApprovalCardShell>
  );
}

function WorkspaceWriteApprovalCard({
  approval,
  onResolve,
}: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const fields = useMemo(
    () => readWorkspaceWriteApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );

  if (!fields) return null;

  return (
    <InlineApprovalCardShell
      Icon={FileText}
      title={workspaceWriteTitle(intl, fields)}
      subtitle={intl.formatMessage({
        defaultMessage: 'This writes to a connected app',
        id: 'KG36cvDpLp',
      })}
      urgent={!isBusy}
      data-testid={`nango-workspace-approval-${approval.approval_id}`}
    >
      <div className="flex flex-col gap-3">
        <LabelledContextPanel
          label={intl.formatMessage({
            defaultMessage: 'What this does',
            id: 'Mno4r2Qwe2',
          })}
        >
          <TypographyP2 className="text-sm text-text-secondary">
            {workspaceWriteSummary(intl, fields)}
          </TypographyP2>
        </LabelledContextPanel>
        {fields.destination ? (
          <LabelledContextPanel
            label={intl.formatMessage({
              defaultMessage: 'Destination',
              id: 'BzQPM+4Est',
            })}
          >
            <TypographyP2
              className="break-all text-sm text-text-secondary"
              data-testid="nango-workspace-approval-destination"
            >
              {fields.destination}
            </TypographyP2>
          </LabelledContextPanel>
        ) : null}
        {fields.preview ? (
          <LabelledContextPanel
            label={intl.formatMessage({
              defaultMessage: 'Preview',
              id: 'TJo5E6JuIN',
            })}
          >
            <TypographyP2
              className="whitespace-pre-wrap break-words text-sm text-text-secondary"
              data-testid="nango-workspace-approval-preview"
            >
              {fields.preview}
            </TypographyP2>
          </LabelledContextPanel>
        ) : null}
        <InlineApprovalActions
          onApprove={() => submit('approve')}
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
        />
      </div>
    </InlineApprovalCardShell>
  );
}

const workspaceWriteTitle = (
  intl: IntlShape,
  fields: WorkspaceWriteApprovalFields
): string => {
  const action = fields.action;
  if (action === 'create') {
    return intl.formatMessage({
      defaultMessage: 'Create in connected app',
      id: '4DY8qH7mOW',
    });
  }
  if (action === 'delete') {
    return intl.formatMessage({
      defaultMessage: 'Delete in connected app',
      id: 'qhI2kll9ST',
    });
  }
  if (action === 'send') {
    return intl.formatMessage({
      defaultMessage: 'Send to connected app',
      id: 'AQ+TNsgp4c',
    });
  }
  return intl.formatMessage({
    defaultMessage: 'Edit in connected app',
    id: '889QBJ/YjG',
  });
};

const workspaceWriteSummary = (
  intl: IntlShape,
  fields: WorkspaceWriteApprovalFields
): string => {
  const actionLabel = workspaceActionLabel(intl, fields.action);
  const targetLabel = workspaceTargetLabel(intl, fields.target);
  return intl.formatMessage(
    {
      defaultMessage: '{action} a {target}.',
      id: 'pPDmY2WqRO',
    },
    { action: actionLabel, target: targetLabel }
  );
};

const workspaceActionLabel = (
  intl: IntlShape,
  action: NangoWorkspaceWriteAction
): string => {
  if (action === 'create') {
    return intl.formatMessage({ defaultMessage: 'Create', id: 'VzzYJkHnLP' });
  }
  if (action === 'delete') {
    return intl.formatMessage({ defaultMessage: 'Delete', id: 'K3r6DQW7h+' });
  }
  if (action === 'send') {
    return intl.formatMessage({ defaultMessage: 'Send', id: '9WRlF4R2gm' });
  }
  return intl.formatMessage({ defaultMessage: 'Edit', id: 'wEQDC6Wv3/' });
};

const workspaceTargetLabel = (
  intl: IntlShape,
  target: WorkspaceWriteApprovalFields['target']
): string => {
  if (target === 'spreadsheet') {
    return intl.formatMessage({ defaultMessage: 'spreadsheet', id: '9Ushpsi4Al' });
  }
  if (target === 'page') {
    return intl.formatMessage({ defaultMessage: 'page', id: 'ovCNnFs/tg' });
  }
  if (target === 'message') {
    return intl.formatMessage({ defaultMessage: 'message', id: '+Nr1ejNHzE' });
  }
  if (target === 'file') {
    return intl.formatMessage({ defaultMessage: 'file', id: 'vwxMsyPVYx' });
  }
  return intl.formatMessage({ defaultMessage: 'document', id: 'fVrLDpHixQ' });
};
